import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Play, X } from "lucide-react";
import { Container, Reveal, PageBanner, siteImg } from "@/site/ui";

function parseVideo(url = "") {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { embed: `https://www.youtube.com/embed/${yt[1]}?autoplay=1`, thumb: `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg` };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { embed: `https://player.vimeo.com/video/${vm[1]}?autoplay=1`, thumb: "" };
  return { embed: url, thumb: "" };
}

export default function Videos() {
  const c = useOutletContext();
  const v = c.videos || {};
  const items = v.items || [];
  const [active, setActive] = useState(null);

  return (
    <div data-testid="videos-page">
      <PageBanner image={c.banners?.videos} eyebrow="Videos" title={v.heading || "Videos"} subtitle={v.subtitle} />

      <section className="py-16 md:py-24">
        <Container>
          {items.length === 0 ? (
            <div className="h-40 flex items-center justify-center rounded-2xl border border-dashed border-gray-200 text-[#A3A3A3]">No videos yet.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((it, i) => {
                const info = parseVideo(it.url);
                const thumb = it.thumbnail || info.thumb;
                return (
                  <Reveal key={it.id || i} delay={(i % 3) * 0.06}>
                    <button onClick={() => setActive({ ...it, embed: info.embed })} data-testid={`video-${i}`}
                      className="group block w-full overflow-hidden rounded-2xl border border-gray-100 bg-black text-left">
                      <div className="relative aspect-video">
                        {thumb ? <img src={siteImg(thumb)} alt={it.title} className="h-full w-full object-cover opacity-90 transition-opacity duration-300 group-hover:opacity-100" />
                          : <div className="h-full w-full bg-[#0A0A0A]" />}
                        <span className="absolute inset-0 flex items-center justify-center">
                          <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[var(--rgh-primary)] text-white shadow-lg transition-transform duration-300 group-hover:scale-110">
                            <Play size={26} fill="currentColor" />
                          </span>
                        </span>
                      </div>
                      {it.title && <p className="bg-white p-4 text-sm font-semibold text-[#0A0A0A]">{it.title}</p>}
                    </button>
                  </Reveal>
                );
              })}
            </div>
          )}
        </Container>
      </section>

      {active && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4" onClick={() => setActive(null)} data-testid="video-modal">
          <button className="absolute top-6 right-6 text-white/80 hover:text-white" onClick={() => setActive(null)}><X size={28} /></button>
          <div className="w-full max-w-4xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <iframe title={active.title || "Video"} src={active.embed} className="h-full w-full rounded-xl" allow="autoplay; encrypted-media; fullscreen" allowFullScreen />
          </div>
        </div>
      )}
    </div>
  );
}
